import { Dispatch, SetStateAction } from 'react';
import { addDays } from 'date-fns';
import { useFormApi, useFormState } from 'informed';
import { useAchievementTypes, useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { getColumnOptionsByProductType } from 'shared-helpers/analytics';
import { traverseNestedObjectAndGrabValue } from 'shared-helpers/common';
import { FlexContainer } from 'components/common';
import { useFiltersContext } from 'components/common/filter';
import {
  Checkbox,
  Datepicker,
  FormGroup,
  RadioGroup,
  Select
} from 'components/common/form';

import type { GetUsersPageFilterContentsRes } from 'shared-types/responses';
import type { ExporExcelFormState } from './UsersExportExcel';

interface Props {
  setValidColumnSelection: Dispatch<SetStateAction<boolean>>;
}

const UsersExportExcelOptions = ({ setValidColumnSelection }: Props) => {
  const formApi = useFormApi<ExporExcelFormState>();
  const formState = useFormState<ExporExcelFormState>();

  const { language, messages, serverConfig, theme, user } = useReduxState();

  const { data } = useFiltersContext();

  const achievementTypes = useAchievementTypes();

  const selectedType = formState?.values?.type;
  if (!selectedType) return null;

  const getSelectedColumns = (): string[] => {
    const columns = formState?.values?.columns || {};
    const columnOptionsSelected = [];

    const isSelected = (baseString: string, key: string) => {
      const fieldValue = traverseNestedObjectAndGrabValue(
        formState.values,
        `${baseString}.${key}`
      );

      const option = `${baseString}.${key}`.replace('columns.', '');
      if (typeof fieldValue === 'boolean' && fieldValue) {
        columnOptionsSelected.push(option);
      } else {
        Object.keys(fieldValue).forEach((subKey) => {
          isSelected(`${baseString}.${key}`, subKey);
        });
      }
    };

    Object.keys(columns).forEach((key) => {
      isSelected('columns', key);
    });

    return columnOptionsSelected;
  };

  const handleColumnChange = (column: string, value: boolean) => {
    const sortByColumns = ['sortByPrimary', 'sortBySecondary'];

    for (const sort of sortByColumns) {
      if (formState.values?.[sort] === column && !value) {
        const findOption = columnOptions.find(
          (option) =>
            option.value !== column &&
            columnOptionsSelected.includes(option.value)
        );
        if (findOption) formApi.setValue(sort, findOption.value);
        else formApi.setValue(sort, undefined);
      }
    }
  };

  const updateCheckboxes = (
    field: string,
    value: boolean,
    validate?: () => void
  ) => {
    const setField = (baseString: string, key: string, value: boolean) => {
      const fieldValue = traverseNestedObjectAndGrabValue(
        formState.values,
        `${baseString}.${key}`
      );
      if (typeof fieldValue === 'object') {
        Object.keys(fieldValue).forEach((subKey) => {
          setField(`${baseString}.${key}`, subKey, value);
        });
      } else {
        formApi.setValue(`${baseString}.${key}`, value);
      }
    };

    Object.keys(formState?.values[field] || {}).forEach((key) => {
      setField(field, key, value);
    });

    if (!value && field === 'columns') {
      formApi.setValue('sortByPrimary', undefined);
      formApi.setValue('sortBySecondary', undefined);
    }

    if (validate) validate();
  };

  const validateSelectedColumns = () => {
    setTimeout(() => {
      const isValid = Boolean(getSelectedColumns().length);
      setValidColumnSelection(isValid);
    });
  };

  const completionStatusOptions: { label: string; value: string }[] = {
    achievements: [
      { label: messages.commonAll, value: 'all' },
      { label: messages.commonCompleted, value: 'yes' },
      { label: messages.commonValid, value: 'valid' },
      { label: messages.commonExpiring, value: 'expiring' },
      { label: messages.commonExpired, value: 'expired' },
      { label: messages.tasksUsersNotCompleted, value: 'no' }
    ],
    'completion-history': [
      { label: messages.commonAll, value: 'all' },
      { label: messages.commonValid, value: 'valid' },
      { label: messages.commonExpired, value: 'expired' },
      { label: messages.profileTimelineUpcoming, value: 'upcoming' }
    ]
  }[selectedType];

  const products: { label: string; value: number }[] =
    {
      achievements: data?.achievements || [],
      'calendar-events': data?.calendarEvents || [],
      courses: data?.courses || [],
      paths: data?.paths || []
    }[selectedType]?.map(
      (
        product: (
          | GetUsersPageFilterContentsRes['achievements']
          | GetUsersPageFilterContentsRes['calendarEvents']
          | GetUsersPageFilterContentsRes['courses']
          | GetUsersPageFilterContentsRes['paths']
        )[0]
      ) => ({
        label: product.title,
        value: product.id
      })
    ) || [];

  const productStringsAll: string =
    {
      achievements: messages.commonAllAchievements,
      'calendar-events': messages.commonAllCalendarEvents,
      courses: messages.commonAllCourses,
      paths: messages.commonAllPaths
    }[selectedType] || '';

  const productStringsSelected: string =
    {
      achievements: messages.commonSelectedAchievements,
      'calendar-events': messages.commonSelectedCalendarEvents,
      courses: messages.commonSelectedCourses,
      paths: messages.commonSelectedPaths
    }[selectedType] || '';

  const productStringsAllUsersInfo =
    {
      achievements: messages.usersFiltersIncludeAllUsersAchievement,
      'calendar-events': messages.usersFiltersIncludeAllUsersCalendarEvent,
      courses: messages.usersFiltersIncludeAllUsersCourse,
      paths: messages.usersFiltersIncludeAllUsersPath
    }[selectedType] || '';

  const productTypes = [
    ...(serverConfig.hideCoursesFromUI
      ? []
      : [{ text: messages.commonCourse, value: 'course' }]),
    ...(serverConfig.hidePathsFromUI
      ? []
      : [{ text: messages.commonPath, value: 'path' }]),
    ...(serverConfig.hideCalendarEventsFromUI
      ? []
      : [{ text: messages.eventCalendarEvent, value: 'calendar-event' }]),
    ...(serverConfig.hideAchievementsFromUI
      ? []
      : Object.keys(achievementTypes).map((achievementType) => ({
          text: achievementTypes[achievementType].title as string,
          value: achievementType
        }))),
    ...(serverConfig.individualAchievementsEnabled &&
    !serverConfig.hideAchievementsFromUI
      ? [
          {
            text: messages.individualAchievements,
            value: 'individual-achievement'
          }
        ]
      : [])
  ].sort((a, b) => (a.text > b.text ? 1 : -1));

  const productTypesSelected = Object.keys(
    formState.values?.productTypes || {}
  ).some((key) => formState.values?.productTypes[key]);

  const columnOptions =
    getColumnOptionsByProductType({
      language,
      messages,
      requestUser: user,
      serverConfig,
      type: selectedType
    }) || [];

  const columnOptionsSelected = getSelectedColumns();

  const sortOptions = columnOptions.filter(({ value }) =>
    columnOptionsSelected.includes(value)
  );

  return (
    <div className="options">
      <div
        className="products"
        style={{ display: products.length ? 'block' : 'none' }}
      >
        <FormGroup label={messages.commonInclude} maxWidth="100%">
          <RadioGroup
            field="productAllOrSelected"
            inline={true}
            options={[
              {
                id: 'export-excel-product-type-all',
                label: productStringsAll,
                value: 'all'
              },
              {
                id: 'export-excel-product-type-selected',
                label: productStringsSelected,
                value: 'selected'
              }
            ]}
          />

          <div className="products-selector">
            <Select
              disabled={formState?.values?.productAllOrSelected === 'all'}
              isMulti={true}
              isSearchable={true}
              field="productIds"
              id="export-excel-product-ids"
              placeholder={messages.attributesSelectFromOptions}
              showLabelForSelectedElement={true}
              options={products}
              value={formState?.values?.productIds?.map((productId) =>
                products.find(({ value }) => value === productId)
              )}
            />
          </div>
        </FormGroup>

        <FormGroup label={messages.commonUsers}>
          <Checkbox
            id="include-all-users"
            field="includeAllUsers"
            label={messages.usersFiltersIncludeAllUsers}
            labelTooltip={productStringsAllUsersInfo}
          />
        </FormGroup>

        <div
          style={{ display: selectedType === 'achievements' ? 'flex' : 'none' }}
        >
          <FormGroup
            htmlFor="completion-status"
            label={messages.commonStatus}
            labelIsOptional={true}
            style={{ marginTop: 20, width: 200 }}
          >
            <Select
              field="completionStatus"
              id="export-excel-completion-status"
              options={completionStatusOptions}
            />
          </FormGroup>
        </div>
      </div>

      <div
        className="date-range"
        style={{
          display: selectedType === 'completion-history' ? 'flex' : 'none'
        }}
      >
        <FormGroup
          htmlFor="start-date"
          label={messages.analyticsDateFrom}
          labelIsOptional={true}
        >
          <Datepicker
            field="fromDate"
            id="export-excel-from-date"
            isClearable={true}
            placeholder={messages.analyticsDateFrom}
            simpleClear={true}
            style={{ width: 200 }}
          />
        </FormGroup>

        <FormGroup
          htmlFor="end-date"
          label={messages.analyticsDateTo}
          labelIsOptional={true}
          style={{ marginTop: 0 }}
        >
          <Datepicker
            field="toDate"
            id="export-excel-to-date"
            isClearable={true}
            minDate={
              formState?.values.fromDate
                ? addDays(formState?.values.fromDate, 1)
                : undefined
            }
            placeholder={messages.analyticsDateTo}
            simpleClear={true}
            style={{ width: 200 }}
          />
        </FormGroup>

        <FormGroup
          htmlFor="completion-status"
          label={messages.commonStatus}
          labelIsOptional={true}
          style={{ marginTop: 0, width: 200 }}
        >
          <Select
            field="completionStatus"
            id="export-excel-completion-status"
            options={completionStatusOptions}
          />
        </FormGroup>
      </div>

      <div
        className="product-types"
        style={{
          display: selectedType === 'completion-history' ? 'block' : 'none'
        }}
      >
        <FormGroup
          label={messages.commonTypes}
          labelAction={
            <button
              className="toggle-button"
              onClick={() =>
                updateCheckboxes('productTypes', !productTypesSelected)
              }
              type="button"
            >
              {productTypesSelected
                ? messages.commonClearAll
                : messages.commonSelectAll}
            </button>
          }
          maxWidth="100%"
          style={{ marginBottom: 25, marginTop: 0 }}
        >
          <div className="columns columns-three">
            {productTypes.map((productType) => (
              <div className="column" key={productType.value}>
                <Checkbox
                  field={`productTypes.${productType.value}`}
                  id={`export-excel-product-type-${productType.value}`}
                  initialValue={true}
                  label={productType.text}
                />
              </div>
            ))}
          </div>
        </FormGroup>
      </div>

      <div className="column-selection">
        <FormGroup
          label={messages.commonColumns}
          labelAction={
            <button
              className="toggle-button"
              onClick={() =>
                updateCheckboxes(
                  'columns',
                  !columnOptionsSelected.length,
                  validateSelectedColumns
                )
              }
              type="button"
            >
              {columnOptionsSelected.length
                ? messages.commonClearAll
                : messages.commonSelectAll}
            </button>
          }
          maxWidth="100%"
        >
          <div
            className={`columns ${
              columnOptions.length > 11
                ? 'columns-three'
                : columnOptions.length > 5
                ? 'columns-two'
                : ''
            }`}
          >
            {columnOptions.map((option, index) => (
              <div className="column" key={`${index}-${option.value}`}>
                <Checkbox
                  field={`columns.${option.value}`}
                  id={`export-excel-column-${index}-${option.value}`}
                  initialValue={true}
                  label={option.label}
                  onClick={validateSelectedColumns}
                  onValueChange={(value: boolean) =>
                    handleColumnChange(option.value, value)
                  }
                />
              </div>
            ))}
          </div>
        </FormGroup>

        {!columnOptionsSelected.length ? (
          <div className="error-message">
            {messages.commonSelectAtLeastOneOption}
          </div>
        ) : null}
      </div>

      <div className="option-group">
        <div className="sort">
          <FormGroup label={messages.usersExportExcelSortPrimary}>
            <Select
              field="sortByPrimary"
              noOptionsMessage={messages.commonNoOptions}
              options={sortOptions}
              style={{ width: 200 }}
            />
          </FormGroup>

          <FormGroup
            label={messages.usersExportExcelSortSecondary}
            labelTooltip={messages.usersExportExcelSortSecondaryTooltip}
            style={{ marginTop: 0 }}
          >
            <Select
              field="sortBySecondary"
              noOptionsMessage={messages.commonNoOptions}
              options={sortOptions}
              style={{ width: 200 }}
            />
          </FormGroup>
        </div>
      </div>

      <style jsx>{`
        .products {
          margin-bottom: 30px;
        }
        .products-selector {
          margin-top: 10px;
        }
        .columns {
          column-count: 1;
          margin-top: 5px;
        }
        .column {
          margin-bottom: 5px;
        }
        .toggle-button {
          background-color: transparent;
          border: 0;
          color: ${theme.textColorLight};
          cursor: pointer;
          font-size: 1.5rem;
          font-weight: 400;
          outline: 0;
          font-family: inherit;
          padding: 0;
          text-decoration: underline;
          transition: color 0.3s;
        }
        .toggle-button:focus,
        .toggle-button:hover {
          color: ${theme.textColor};
        }
        .option-group {
          display: flex;
          flex-direction: column;
          flex-wrap: wrap;
          gap: 25px 100px;
          margin-top: 25px;
          width: 100%;
        }
        .sort {
          display: flex;
          flex-direction: column;
          gap: 15px 30px;
        }
        .date-range {
          display: flex;
          flex-direction: column;
          gap: 15px 30px;
        }
        .product-types {
          margin-top: 25px;
        }
        .error-message {
          color: ${theme.dangerColor};
          font-size: 1.3rem;
          margin-top: 5px;
          padding: 3px;
        }
        @media (min-width: ${breakpoints.small}px) {
          .sort {
            flex-direction: row;
          }
          .date-range {
            flex-direction: row;
          }
          .columns-two,
          .columns-three {
            column-count: 2;
          }
        }
        @media (min-width: ${breakpoints.medium}px) {
          .option-group {
            flex-direction: row;
          }
          .columns-three {
            column-count: 3;
          }
        }
      `}</style>
    </div>
  );
};

export default UsersExportExcelOptions;
